import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import {
  Save,
  Loader2,
  ShieldCheck,
  Link2,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://api.marketmaestroovip.in';

const PolicyManager = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy');
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [sidebarOpen]);

  // Load policies
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get(`${API_URL}/api/policies`);
        setFormData(res.data.data || {});
      } catch (err) {
        console.error('Error fetching policies:', err);
        setMessage({ type: 'error', text: 'Failed to load policies.' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchPolicies();
  }, []);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      const payload = { ...formData };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;

      const res = await axios.put(`${API_URL}/api/policies`, payload);

      if (res.data.success) {
        setFormData(res.data.data || payload);
        setMessage({ type: 'success', text: 'Saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Update failed.' });
      }
    } catch (err) {
      console.error('Save error:', err);
      setMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  // Tab config
  const tabs = [
    {
      key: 'privacy',
      label: 'Privacy Policy',
      icon: <ShieldCheck className="w-4 h-4" />,
      titleKey: 'privacy_title',
      updatedKey: 'privacy_last_updated',
      contentKey: 'privacy_content'
    },
    {
      key: 'refund',
      label: 'Refund Policy',
      icon: <ShieldCheck className="w-4 h-4" />,
      titleKey: 'refund_title',
      updatedKey: 'refund_last_updated',
      contentKey: 'refund_content'
    },
    {
      key: 'charter',
      label: 'Investor Charter',
      icon: <Link2 className="w-4 h-4" />,
      linkKey: 'investor_charter_link'
    }
  ];

  const currentTab = tabs.find((t) => t.key === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="flex h-screen">
        <Sidebar isOpen={sidebarOpen} />

        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Title Bar */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                  Manage Policies
                </h1>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                  Edit Privacy Policy, Refund Policy, and the Investor Charter link.
                </p>
              </div>

              <button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" /> Save Changes
                  </>
                )}
              </button>
            </div>

            {/* Status Message */}
            {message && (
              <div
                className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {message.type === 'success' && <CheckCircle2 className="w-4 h-4" />}
                {message.text}
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 flex-wrap">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition border-b-2 ${
                        activeTab === tab.key
                          ? 'border-blue-600 text-blue-700 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="p-6 space-y-5">
                  {currentTab.key === 'charter' ? (
                    /* Investor Charter — single input only */
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Investor Charter Link
                      </label>
                      <input
                        type="url"
                        value={formData[currentTab.linkKey] || ''}
                        onChange={(e) =>
                          handleChange(currentTab.linkKey, e.target.value)
                        }
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                        placeholder="https://example.com/investor-charter.pdf"
                      />
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Paste the full URL (PDF or webpage) for the Investor Charter.
                        This link will be used on the public site.
                      </p>

                      {formData[currentTab.linkKey] && (
                        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Current link:
                          </p>
                          <a
                            href={formData[currentTab.linkKey]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 break-all underline"
                          >
                            {formData[currentTab.linkKey]}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Privacy / Refund tabs — same as before */
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Title
                        </label>
                        <input
                          type="text"
                          value={formData[currentTab.titleKey] || ''}
                          onChange={(e) =>
                            handleChange(currentTab.titleKey, e.target.value)
                          }
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                          placeholder={`e.g. ${currentTab.label}`}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Last Updated
                        </label>
                        <input
                          type="text"
                          value={formData[currentTab.updatedKey] || ''}
                          onChange={(e) =>
                            handleChange(currentTab.updatedKey, e.target.value)
                          }
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                          placeholder="e.g. January 2026"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Content
                        </label>
                        <textarea
                          rows={20}
                          value={formData[currentTab.contentKey] || ''}
                          onChange={(e) =>
                            handleChange(currentTab.contentKey, e.target.value)
                          }
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition font-mono text-sm leading-relaxed"
                          placeholder="Write plain paragraphs. Use Enter for new lines and blank lines between paragraphs."
                        />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Line breaks and blank lines you type here will appear exactly the same on the public page.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Bottom Save */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" /> Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default PolicyManager;